import { theme } from 'lib/theme';
import * as React from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { Button } from './Button';
import { BlurView } from 'expo-blur';

type Props<K extends string> = {
  /**
   * Return false to not call setActiveKey internally
   */
  onActiveChange: (activeItem: K) => boolean | void;
  buttons: { text: string; key: K; rbs?: boolean; disabled?: boolean }[];
  active: string;
  containerStyle?: StyleProp<ViewStyle>;
};

export const ButtonToggleGroup = <K extends string>({
  buttons,
  active,
  onActiveChange,
  containerStyle,
}: Props<K>): React.ReactElement => {
  const [activeKey, setActiveKey] = React.useState(active);
  const lastIncomingActive = React.useRef(active);

  const handleOnPress = React.useCallback(
    (newActiveKey: K) => {
      const afterChange = onActiveChange(newActiveKey);
      if (afterChange !== false) {
        setActiveKey(newActiveKey);
      }
    },
    [onActiveChange],
  );

  const ButtonsComponent = React.useMemo(
    () =>
      buttons.map((button) => {
        const isActive = button.key === activeKey;
        return (
          <Button
            key={button.key}
            text={button.text}
            disabled={button.disabled}
            onPress={isActive ? undefined : () => handleOnPress(button.key)}
            style={{ flex: 1, opacity: 1 }}
            textStyle={[{ color: theme.colors.text }]}
            backgroundColor={isActive ? theme.colors.white : 'transparent'}
            styleHovered={{ backgroundColor: 'transparent' }}
            stylePressed={{ backgroundColor: 'transparent' }}
            rbs={button.rbs}
            glass={false}
          />
        );
      }),
    [buttons, activeKey, handleOnPress],
  );

  React.useEffect(() => {
    if (lastIncomingActive.current !== active) {
      setActiveKey(active);
      lastIncomingActive.current = active;
    }
  }, [active]);

  return (
    <BlurView
      intensity={10}
      style={[
        {
          borderRadius: 99,
          overflow: 'hidden',
        },
        containerStyle,
      ]}
    >
      <View
        style={{
          borderRadius: 99,
          padding: 2,
          backgroundColor: theme.colors.black10,
          flexDirection: 'row',
        }}
      >
        {ButtonsComponent}
      </View>
    </BlurView>
  );
};
